import {
  IContextualMenuItem,
  IContextualMenuItemProps,
  IContextualMenuItemRenderFunctions,
  IContextualMenuProps,
} from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPActionButton, IPFontIcon, IPIcon, IPPrimaryButton, IPSearchBox, IPSpinner, IPStack } from 'baseComponents';
import { IPContextualMenuItemType } from 'baseComponents/ContextualMenu/IPContextualMenu';
import LinkCreatorURL from 'components/Links/Creators/LinkCreatorURL';
import VirtualContentPicker from 'components/Pickers/VirtualContentPicker';
import { getSearchableMenuProps } from 'components/Utils/SearchableMenuList';
import { globalFilterDelay } from 'globalConstants';
import { checkIcon, globalStackTokensGapSmall, newIcon, recentIcon } from 'globalStyles';
import { PermissionTypes } from 'models/auth/rolePermission';
import ResourceLink from 'models/resourceLink';
import { DocumentProviderType, isBrokeredProvider } from 'models/documentProvider';
import ResourceList, { ResourceListType } from 'models/resourceList';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { apiGetLinks, apiGetLinksForList, apiGetRecentlyReadLinks } from 'services/Api/linkService';
import { apiRequest } from 'services/Auth/authConfig';
import { hasUserFeatureGenericManager, hasUserRolePermission } from 'services/Auth/featurePermissions';
import { sortOnString } from 'utils/sorting';
import LinkCreatorBrokered from './Creators/LinkCreatorBrokered';
import LinkCreatorCustomList from './Creators/LinkCreatorCustomList';
import LinkCreatorDocumentLibrary from './Creators/LinkCreatorDocumentLibrary';
import LinkCreatorSitePageLibrary from './Creators/LinkCreatorSitePageLibrary';
import { getListIcon } from './Helpers/LinkIcons';
import HrefLink from './HrefLink';

interface IAddNewLink {
  addLinks: (links: ResourceLink[]) => void;
  existingLinks: ResourceLink[];
  disabled: boolean;
  allowCustomLists: boolean;
  allowDocumentLibraries: boolean;
  allowWebURLs: boolean;
  allowSitePages: boolean;
  allowAddCategory?: boolean;
  listFilter?: ResourceList[];
  entityContext: boolean;
  selectedList?: ResourceList;
  //a folder inside the selected list where the upload starts, such as a task's evidence folder; its name is the
  //path below the list
  uploadFolderId?: string;
  uploadFolderName?: string;
  hideButton?: boolean;
  selectedListDisableOpen?: boolean;
  close?: () => void;
  showAsLink?: boolean;
  showAsLinkLabel?: string;
  hideWholeList?: boolean;
  singleSelect?: boolean;
  showCategoryContents?: boolean;
}

const AddNewLink = (props: IAddNewLink) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['translation', 'library']);
  const history = useHistory();
  const [menuPropsLoading, setMenuPropsLoading] = useState<boolean>(false);
  const [listsLoading, setListsLoading] = useState<boolean>(false);
  const [linksLoading, setLinksLoading] = useState<boolean>(false);
  const [lists, setLists] = useState<ResourceList[]>([]);
  const [selectedListForMenu, setSelectedListForMenu] = useState<ResourceList | undefined>(undefined);
  const [listLinks, setListLinks] = useState<ResourceLink[]>([]);
  const [allListLinks, setAllListLinks] = useState<ResourceLink[]>([]);
  const [linksLoadedForListId, setLinksLoadedForListId] = useState<number | undefined>(undefined);
  const [filteredLists, setFilteredLists] = useState<ResourceList[]>([]);
  const [filteredLinks, setFilteredLinks] = useState<ResourceLink[]>([]);
  const [_searchTimer, setSearchTimer] = useState<NodeJS.Timeout | undefined>(undefined);
  const [searchText, setSearchText] = useState<string | undefined>(undefined);
  const [mostRecentLinks, setMostRecentLinks] = useState<ResourceLink[]>([]);

  const loadListLinks = async (list: ResourceList | undefined) => {
    if (listsLoading) return;

    if (!list) {
      setListLinks([]);
      setLinksLoadedForListId(undefined);

      return;
    }

    try {
      if (linksLoadedForListId === list.listId) return;
      setListsLoading(true);

      const accessToken: string = await appContext.getAccessToken(apiRequest.scopes);
      const _links = await apiGetLinksForList(list.listId, accessToken, appContext.globalDataCache);
      setListLinks(_links);
      setLinksLoadedForListId(list.listId);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setListsLoading(false);
    }
  };

  const loadLinks = async () => {
    if (linksLoading) return;
    try {
      setLinksLoading(true);
      const accessToken: string = await appContext.getAccessToken(apiRequest.scopes);
      const _links = await apiGetLinks(false, accessToken, appContext.globalDataCache);
      const _mostRecentLinks = await apiGetRecentlyReadLinks(10, accessToken, appContext.globalDataCache);

      // This is a workaround for the API returning whole lists as links.
      // See: https://github.com/orgs/redlab-app/projects/4/views/2?pane=issue&itemId=109320769&issue=redlab-app%7Cisoplanner-web%7C2520
      const _linksNoLists = _links.filter((l) => l.isWholeList() === false);
      const _mostRecentLinksNoLists = _mostRecentLinks.filter((l) => l.isWholeList() === false);
      setMostRecentLinks(_mostRecentLinksNoLists);

      setAllListLinks(_linksNoLists);
      setFilteredLinks(_linksNoLists);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setLinksLoading(false);
    }
  };

  const loadData = async () => {
    if (menuPropsLoading) return;
    try {
      setMenuPropsLoading(true);
      let _lists = await appContext.globalDataCache.lists.getItems();

      if (props.listFilter) {
        _lists = _lists.filter((l) => props.listFilter?.some((f) => f.listId === l.listId));
      } else {
        if (!props.allowCustomLists) {
          _lists = _lists.filter((l) => l.listType !== ResourceListType.CustomList);
        }
        if (!props.allowDocumentLibraries) {
          _lists = _lists.filter((l) => l.listType !== ResourceListType.DocumentLibrary);
        }
        if (!props.allowWebURLs) {
          _lists = _lists.filter((l) => l.listType !== ResourceListType.WebURL);
        }
        if (!props.allowSitePages) {
          _lists = _lists.filter((l) => l.listType !== ResourceListType.SitePageLibrary);
        }
      }

      setLists(_lists);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setMenuPropsLoading(false);
    }
  };

  //
  // Effects
  //
  useEffect(() => {
    loadData();
    loadLinks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let newFilteredLists: ResourceList[] = [...lists];
    let newFilteredLinks: ResourceLink[] = [...allListLinks];
    //apply search text
    if (searchText && props.showCategoryContents) {
      newFilteredLinks = applyTextFilter(allListLinks, searchText);
      setFilteredLinks(newFilteredLinks);
      newFilteredLists = newFilteredLists.filter((l) => {
        const linkMatch = newFilteredLinks.find((link) => link.listId === l.listId) !== undefined;
        const nameMatch = l.name.toLowerCase().indexOf(searchText.toLowerCase()) >= 0;

        return linkMatch || nameMatch;
      });
    }

    //set state
    setFilteredLinks(newFilteredLinks);
    setFilteredLists(newFilteredLists);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText, lists, allListLinks]);

  useEffect(() => {
    if (!props.disabled) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.disabled]);

  useEffect(() => {
    if (props.selectedList) {
      if (!props.selectedListDisableOpen) {
        setSelectedListForMenu(props.selectedList);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.selectedList]);

  useEffect(() => {
    loadListLinks(selectedListForMenu);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedListForMenu]);

  //
  // Filter
  //
  const applyTextFilter = (links: ResourceLink[], filterText: string): ResourceLink[] => {
    const lcFilterText = filterText.toLowerCase();
    const _filteredListItems = links.filter(
      (link) =>
        link.linkURL.toLowerCase().indexOf(lcFilterText) >= 0 ||
        link.linkName.toLowerCase().indexOf(lcFilterText) >= 0 ||
        link.list.name.toLowerCase().indexOf(lcFilterText) >= 0,
    );

    return _filteredListItems;
  };

  //
  // Helpers
  //
  const navigateToListsAndAddNew = () => {
    history.push('/library/lists#add');
  };

  const onChangeText = (event?: React.ChangeEvent<HTMLInputElement>, filterText?: string): void => {
    if (!filterText) {
      setSearchText(undefined);
    } else {
      setSearchText(filterText);
    }
  };

  const onClose = () => {
    setSelectedListForMenu(undefined);
    if (props.close) props.close();
  };

  const getSubMenuProps = (subMenuLinks: ResourceLink[]): IContextualMenuProps | undefined => {
    return {
      items: subMenuLinks.map((link) => {
        const isExistingLink = props.existingLinks.filter((l) => l.linkId === link.linkId).length > 0;

        return {
          key: link.linkId.toString(),
          text: link.linkName,
          iconProps: isExistingLink ? checkIcon : undefined,
          onClick: () => {
            props.addLinks([link]);
            onClose();
          },
          disabled: isExistingLink,
        };
      }),
    };
  };

  const getFilter = (props: IContextualMenuItemProps, defaultRenders: IContextualMenuItemRenderFunctions) => {
    return (
      <IPSearchBox
        autoFocus={!appContext.isMobileView}
        placeholder={t('translation:General.Filter.Placeholder')}
        underlined
        id="search"
        onChange={(ev, newValue) => {
          if (_searchTimer) {
            clearTimeout(_searchTimer);
          }
          setSearchTimer(setTimeout(() => onChangeText(ev, newValue), globalFilterDelay));
        }}
        styles={{ root: { width: 300 } }}
      />
    );
  };

  const getMenuProps = (): IContextualMenuProps | undefined => {
    let items: IContextualMenuItem[] = [];

    if (!props.selectedList) {
      // Add all categories with links in submenus.
      items = filteredLists
        .sort((a, b) => {
          return sortOnString(a.name, b.name);
        })
        .map((_item) => {
          const subItems = filteredLinks
            .filter((link) => link.listId === _item.listId)
            .sort((a, b) => {
              return sortOnString(a.linkName, b.linkName);
            });
          const subItemCount = subItems.length;
          const showSubMenu = props.showCategoryContents && subItemCount > 0;

          return {
            key: _item.listId.toString(),
            text: `${_item.name}`,
            secondaryText: !showSubMenu ? undefined : `(${subItemCount})`,
            iconProps: getListIcon(_item),
            onClick: () => {
              setSelectedListForMenu(_item);
            },
            split: props.showCategoryContents && showSubMenu,
            subMenuProps: !showSubMenu ? undefined : getSubMenuProps(subItems),
          };
        });

      // Add the filter to the top of the menu if it's enabled and if there are lists.
      if (lists.length > 0 && props.showCategoryContents) {
        items = [
          {
            key: 'search',
            itemType: IPContextualMenuItemType.Header,
            onRenderContent: getFilter,
          },
          {
            key: 'recentitems',
            itemType: IPContextualMenuItemType.Normal,
            text: t('library:LinkComponents.RecentlyOpened'),
            iconProps: recentIcon,
            subMenuProps: getSubMenuProps(mostRecentLinks),
          },
          {
            key: 'divider',
            itemType: IPContextualMenuItemType.Divider,
          },
          ...items,
        ];

        // If there are no lists after filtering, add an empty state.
        if (filteredLists.length == 0 && searchText) {
          items.push({
            key: 'searchempty',
            text: t('library:LinkComponents.NoListsFound'),
          });
        }
      }

      if ((props.allowAddCategory || lists.length === 0) && hasUserFeatureGenericManager(appContext)) {
        const divider: IContextualMenuItem = {
          key: 'div1',
          text: '-',
          itemType: IPContextualMenuItemType.Divider,
        };
        const addCategory: IContextualMenuItem = {
          //empty state
          key: 'empty',
          onClick: () => {
            navigateToListsAndAddNew();
          },
          onRenderContent(props, defaultRenders) {
            return (
              <IPStack horizontal tokens={globalStackTokensGapSmall}>
                <IPFontIcon {...newIcon} style={{ paddingLeft: 5 }} />
                <HrefLink href="/library/lists#add">{t('library:LinkComponents.AddNoLists')}</HrefLink>
              </IPStack>
            );
          },
        };

        if (props.showCategoryContents) {
          // If there's items in the list, add a divider before the empty state.
          if (items.length > 0) items.push(divider);
          items.push(addCategory);
        } else {
          //a menu of categories only has the command on top, as the category menu of the Reports tab
          items = items.length > 0 ? [addCategory, divider, ...items] : [addCategory];
        }
      } else if (items.length === 0 && !searchText) {
        //empty state for non manager users or when not allowed to add
        items.push({
          key: 'empty',
          text: t('library:LinkComponents.NoLists'),
        });
      }

      return {
        calloutProps: {
          calloutMaxHeight: 600,
        },
        items: items,
        //a menu of categories only gets the keyword filter of the long menus; the menu with the category contents
        //has its own filter box, which also searches the links
        ...(props.showCategoryContents ? {} : getSearchableMenuProps({ pinnedKeys: ['empty'] })),
      };
    } else {
      return undefined;
    }
  };

  const getEditors = () => {
    // a category on a document provider browses and uploads through the API broker, whatever
    // kind of items it holds, so it dispatches before the SharePoint list types below.
    // Microsoft Loop is a pasted link and nothing else, so it falls through to the creator for
    // its list type. This is a transport choice, so it goes by the provider and not by the
    // capabilities: those read all-false while the provider cache is unavailable, and a brokered
    // category sent to the SharePoint creator would hand provider ids to Graph.
    if (
      selectedListForMenu &&
      isBrokeredProvider(selectedListForMenu) &&
      selectedListForMenu.providerType !== DocumentProviderType.MicrosoftLoop
    ) {
      return (
        <IPStack.Item>
          <LinkCreatorBrokered
            isOpen={true}
            list={selectedListForMenu}
            uploadFolderId={props.uploadFolderId}
            uploadFolderName={props.uploadFolderName}
            dismiss={() => onClose()}
            addLinks={props.addLinks}
            listLinks={listLinks}
            existingLinks={props.existingLinks.filter((l) => l.listId === selectedListForMenu.listId)}
            listLoading={listsLoading}
            hideWholeList={props.hideWholeList}
            singleSelect={props.singleSelect}
          />
        </IPStack.Item>
      );
    }

    if (selectedListForMenu?.isVirtual) {
      return (
        <VirtualContentPicker
          list={selectedListForMenu}
          isOpen={true}
          onClose={() => {
            onClose();
          }}
          onSelect={(linkRows) => {
            props.addLinks(linkRows.map((l) => l.item));
            onClose();
          }}
          existingLinks={props.existingLinks}
        />
      );
    }

    return (
      <IPStack.Item>
        {selectedListForMenu?.listType === ResourceListType.WebURL && (
          <LinkCreatorURL
            isOpen={selectedListForMenu !== undefined}
            list={selectedListForMenu}
            dismiss={() => onClose()}
            addLinks={props.addLinks}
            listLinks={listLinks}
            existingLinks={props.existingLinks.filter((l) => l.listId === selectedListForMenu.listId)}
            listLoading={listsLoading}
            allowPickExistingLink={props.entityContext}
            hideWholeList={props.hideWholeList}
            singleSelect={props.singleSelect}
          />
        )}
        {selectedListForMenu?.listType === ResourceListType.CustomList && (
          <LinkCreatorCustomList
            isOpen={selectedListForMenu !== undefined}
            list={selectedListForMenu}
            dismiss={() => onClose()}
            addLinks={props.addLinks}
            listLinks={listLinks}
            existingLinks={props.existingLinks.filter((l) => l.listId === selectedListForMenu.listId)}
            listLoading={listsLoading}
            hideWholeList={props.hideWholeList}
            singleSelect={props.singleSelect}
          />
        )}
        {selectedListForMenu?.listType === ResourceListType.DocumentLibrary && (
          <LinkCreatorDocumentLibrary
            isOpen={selectedListForMenu !== undefined}
            list={selectedListForMenu}
            uploadFolderId={props.uploadFolderId}
            uploadFolderName={props.uploadFolderName}
            dismiss={() => onClose()}
            addLinks={props.addLinks}
            listLinks={listLinks}
            existingLinks={props.existingLinks.filter((l) => l.listId === selectedListForMenu.listId)}
            listLoading={listsLoading}
            hideWholeList={props.hideWholeList}
            singleSelect={props.singleSelect}
          />
        )}
        {selectedListForMenu?.listType === ResourceListType.SitePageLibrary && (
          <LinkCreatorSitePageLibrary
            isOpen={selectedListForMenu !== undefined}
            list={selectedListForMenu}
            dismiss={() => onClose()}
            addLinks={props.addLinks}
            listLinks={listLinks}
            existingLinks={props.existingLinks.filter((l) => l.listId === selectedListForMenu.listId)}
            listLoading={listsLoading}
            entityContext={props.entityContext}
            hideWholeList={props.hideWholeList}
            singleSelect={props.singleSelect}
          />
        )}
      </IPStack.Item>
    );
  };

  //
  // Main render
  //
  if (props.showAsLink) {
    return (
      <IPStack>
        {!props.hideButton && (
          <IPStack.Item>
            <IPActionButton
              iconProps={newIcon}
              className="isoplanner-library-addnew"
              text={props.showAsLinkLabel ?? t('library:LinkComponents.Add')}
              menuProps={getMenuProps()}
              onClick={() => {
                if (!props.selectedList) return;
                setSelectedListForMenu(props.selectedList);
              }}
              disabled={
                props.disabled ||
                menuPropsLoading ||
                listsLoading ||
                !hasUserRolePermission(appContext, PermissionTypes.CreateLibraryItem)
              }
              onAfterMenuDismiss={() => {
                setSearchText(undefined);
              }}
              onRenderMenuIcon={() => {
                if (props.selectedList) return null;
                if (menuPropsLoading) return <IPSpinner />;

                return <IPIcon iconName={'ChevronDown'} />;
              }}
            />
          </IPStack.Item>
        )}
        {getEditors()}
      </IPStack>
    );
  } else {
    return (
      <IPStack>
        {!props.hideButton && (
          <IPStack.Item>
            <IPPrimaryButton
              className="isoplanner-library-addnew"
              text={t('library:LinkComponents.Add')}
              menuProps={getMenuProps()}
              onClick={() => {
                if (!props.selectedList) return;
                setSelectedListForMenu(props.selectedList);
              }}
              onAfterMenuDismiss={() => {
                setSearchText(undefined);
              }}
              disabled={
                props.disabled ||
                menuPropsLoading ||
                listsLoading ||
                linksLoading ||
                !hasUserRolePermission(appContext, PermissionTypes.CreateLibraryItem)
              }
              onRenderMenuIcon={() => {
                if (props.selectedList) return null;
                if (menuPropsLoading || listsLoading) return <IPSpinner />;

                return <IPIcon iconName={'ChevronDown'} />;
              }}
            ></IPPrimaryButton>
          </IPStack.Item>
        )}
        {getEditors()}
      </IPStack>
    );
  }
};

export default AddNewLink;
